import {
  rmSpaces,
  toCamelCase,
  toSnakeCase
} from '@/modules/globalModule/components/helperMethods';
import { NodeGenStrategy } from './nodeGenStrategy';
import JSZip from 'jszip';
import { V4UUID } from '@/firebase/database';
import {
  getMsgDependency,
  getNodeDependency,
  getNodeServiceDependencies,
  getTopicDependency,
  ServiceDependencyInfo
} from './dependenciesGetter';
import { CustomCppNodeText } from '@/modules/nodesCanvasModule/components/storeClasses/node';

/**
 * Code generating strategy for turning nodes into c++ code
 */
export class CppNodeGenStrategy extends NodeGenStrategy {
  /**
   * The zip file to generate the code in. This should be the
   * root folder of the node's package
   * @param zip
   */
  generateCodeZip(zip: JSZip) {
    // generating H File Zip
    const hFileText = this.generateHFileString();
    const nodeInfo = getNodeDependency(this.workspaceId, this.node.id);
    if (!nodeInfo) throw new Error(`node info not found: ${this.node.name}`);
    const includeFolder: JSZip | null = zip.folder('include');
    if (includeFolder) {
      const includeWs: JSZip | null = includeFolder.folder(
        toSnakeCase(nodeInfo.packageName)
      );
      if (includeWs)
        includeWs.file(`${toCamelCase(this.node.name)}.hpp`, hFileText);
    }
    const cppFileText = this.generateCppFileString();
    const srcFolder: JSZip | null = zip.folder('src');
    if (srcFolder)
      srcFolder.file(`${toCamelCase(this.node.name)}.cpp`, cppFileText);
  }

  ////////////////////////////////////////////////////////////////////////////
  //                            H file generation                           //
  ////////////////////////////////////////////////////////////////////////////

  generateHFileString(): string {
    const nodeInfo = getNodeDependency(this.workspaceId, this.node.id);
    if (!nodeInfo) throw new Error(`node info not found: ${this.node.name}`);
    const customNodeText = this.node.customText as CustomCppNodeText;
    return `
#include "rclcpp/rclcpp.hpp"
${this.getMsgImports()}${this.getSrvImports()}
// add custom imports below
${customNodeText.importText}
// end custom imports
namespace ${toSnakeCase(nodeInfo.packageName)} {
class ${toCamelCase(this.node.name)} : public rclcpp::Node
{
    public:
        ${toCamelCase(this.node.name)}();
        // add custom public methods below
${customNodeText.publicMethodsDefText || ''}
        // end custom public methods
    private:
${
  this.getCallbackDefinitions() +
  this.getPublisherDefinitions() +
  this.getSubscriberDefinitions() +
  this.getServerDefinitions() +
  this.getClientDefinitions() +
  this.getSrvCallbackDefinitions()
}
        // add custom private methods below
${customNodeText.privateMethodsDefText || ''}
        // end custom private methods
};
}
`;
  }

  getMsgImports(): string {
    const msgIdsUsed: Array<V4UUID> = [];
    this.node.subscribers
      .concat(this.node.publishers)
      .forEach((subscriberId: V4UUID) => {
        const topicInfo = getTopicDependency(this.workspaceId, subscriberId);
        if (topicInfo && !msgIdsUsed.includes(topicInfo.topic.msgType))
          msgIdsUsed.push(topicInfo.topic.msgType);
      });
    return msgIdsUsed.reduce((accumulator: string, msgId: V4UUID) => {
      let msgInfo = getMsgDependency(this.workspaceId, msgId);
      if (!msgInfo) return accumulator;
      return (
        accumulator +
        `#include "${toSnakeCase(msgInfo.packageName)}/msg/${toSnakeCase(
          msgInfo.msg.name
        )}.hpp"\n`
      );
    }, '');
  }

  getSrvImports(): string {
    const dependencies = getNodeServiceDependencies(
      this.workspaceId,
      this.node.id
    );
    return dependencies.clients
      .concat(dependencies.servers)
      .reduce((accumulator: string, srvInfo: ServiceDependencyInfo) => {
        return (
          accumulator +
          `#include "${toSnakeCase(srvInfo.packageName)}/srv/${toSnakeCase(
            srvInfo.srv.name
          )}.hpp"\n`
        );
      }, '');
  }

  getCallbackDefinitions(): string {
    return this.node.subscribers.reduce(
      (accumulator: string, subscriberId: V4UUID) => {
        const topicInfo = getTopicDependency(this.workspaceId, subscriberId);
        if (!topicInfo) return accumulator;
        const msgInfo = getMsgDependency(
          this.workspaceId,
          topicInfo.topic.msgType
        );
        if (!msgInfo) return accumulator;
        return (
          accumulator +
          `        void ${toSnakeCase(
            topicInfo.topic.name
          )}_callback(const std::shared_ptr<${msgInfo.packageName}::msg::${
            msgInfo.msg.name
          }> msg);\n`
        );
      },
      ''
    );
  }

  getSrvCallbackDefinitions(): string {
    return getNodeServiceDependencies(
      this.workspaceId,
      this.node.id
    ).servers.reduce((accumulator: string, srvInfo: ServiceDependencyInfo) => {
      return (
        accumulator +
        `        void ${toSnakeCase(
          srvInfo.srv.name
        )}_srv_callback(const std::shared_ptr<${toSnakeCase(
          srvInfo.packageName
        )}::srv::${toCamelCase(srvInfo.srv.name)}::Request> request,
            std::shared_ptr<${toSnakeCase(
              srvInfo.packageName
            )}::srv::${toCamelCase(srvInfo.srv.name)}::Response> response);\n`
      );
    }, '');
  }

  getServerDefinitions(): string {
    return getNodeServiceDependencies(
      this.workspaceId,
      this.node.id
    ).servers.reduce((accumulator: string, srvInfo: ServiceDependencyInfo) => {
      return (
        accumulator +
        `        rclcpp::Service<${toSnakeCase(
          srvInfo.packageName
        )}::srv::${toCamelCase(srvInfo.srv.name)}>::SharedPtr ${toSnakeCase(
          srvInfo.srv.name
        )}_server;\n`
      );
    }, '');
  }
  getClientDefinitions(): string {
    return getNodeServiceDependencies(
      this.workspaceId,
      this.node.id
    ).clients.reduce((accumulator: string, srvInfo: ServiceDependencyInfo) => {
      return (
        accumulator +
        `        rclcpp::Client<${toSnakeCase(
          srvInfo.packageName
        )}::srv::${toCamelCase(srvInfo.srv.name)}>::SharedPtr ${toSnakeCase(
          srvInfo.srv.name
        )}_client;\n`
      );
    }, '');
  }

  getPublisherDefinitions(): string {
    return this.node.publishers.reduce(
      (accumulator: string, publisherId: V4UUID) => {
        const topicInfo = getTopicDependency(this.workspaceId, publisherId);
        if (!topicInfo) return accumulator;
        const msgInfo = getMsgDependency(
          this.workspaceId,
          topicInfo.topic.msgType
        );
        if (!msgInfo) return accumulator;
        return (
          accumulator +
          `        rclcpp::Publisher<${msgInfo.packageName}::msg::${
            msgInfo.msg.name
          }>::SharedPtr ${toSnakeCase(topicInfo.topic.name)}_pub;\n`
        );
      },
      ''
    );
  }
  getSubscriberDefinitions(): string {
    return this.node.subscribers.reduce(
      (accumulator: string, subscriberId: V4UUID) => {
        const topicInfo = getTopicDependency(this.workspaceId, subscriberId);
        if (!topicInfo) return accumulator;
        const msgInfo = getMsgDependency(
          this.workspaceId,
          topicInfo.topic.msgType
        );
        if (!msgInfo) return accumulator;
        return (
          accumulator +
          `        rclcpp::Subscription<${msgInfo.packageName}::msg::${
            msgInfo.msg.name
          }>::SharedPtr ${toSnakeCase(topicInfo.topic.name)}_sub;\n`
        );
      },
      ''
    );
  }

  ////////////////////////////////////////////////////////////////////////////
  //                           cpp file generation                          //
  ////////////////////////////////////////////////////////////////////////////

  generateCppFileString(): string {
    const nodeInfo = getNodeDependency(this.workspaceId, this.node.id);
    if (!nodeInfo) throw new Error('Could not find info on node');
    return `
#include "${toSnakeCase(nodeInfo.packageName)}/${toCamelCase(
      this.node.name
    )}.hpp"

namespace ${toSnakeCase(nodeInfo.packageName)} {

${toCamelCase(this.node.name)}::${toCamelCase(
      this.node.name
    )}() : Node("${toSnakeCase(this.node.name)}") {
${
  this.getSubInitialisations() +
  this.getPubInitialisations() +
  this.getServerInitialisations() +
  this.getClientInitialisations()
}
    // add additional constructor functionality below
${this.node.customText.constructorText}
    // end additional constructor functionality
}

${this.getCallbackImplementations()}${this.getSrvCallbackImplementations()}
// add custom method implementations below
${this.node.customText.methodsText}
// end custom method implementations
}
int main(int argc, char * argv[])
{
    // customize main function below
${
  this.node.customText.mainText === ''
    ? `    // Note: editing any of the generated code here could break the node.
    // take precaution when editing
    rclcpp::init(argc, argv);
    rclcpp::spin(std::make_shared<${toSnakeCase(
      nodeInfo.packageName
    )}::${toCamelCase(this.node.name)}>());
    rclcpp::shutdown();
    return 0;`
    : this.node.customText.mainText
}
    // end main customization
}
`;
  }

  /**
   * gets the nodes subscriber initialisation text,
   * for use in the nodes constructor
   */
  getSubInitialisations(): string {
    return this.node.subscribers.reduce(
      (accumulator: string, subscriberId: V4UUID) => {
        const topicInfo = getTopicDependency(this.workspaceId, subscriberId);
        if (!topicInfo) return accumulator;
        const msgInfo = getMsgDependency(
          this.workspaceId,
          topicInfo.topic.msgType
        );
        if (!msgInfo) return accumulator;
        return (
          accumulator +
          `    ${toSnakeCase(
            topicInfo.topic.name
          )}_sub = this->create_subscription<${msgInfo.packageName}::msg::${
            msgInfo.msg.name
          }>
    ("${topicInfo.topic.getNameNoFirstSlash()}", 10, std::bind(&${toCamelCase(
            this.node.name
          )}::${toSnakeCase(
            topicInfo.topic.name
          )}_callback, this, std::placeholders::_1));\n`
        );
      },
      ''
    );
  }

  getPubInitialisations(): string {
    return this.node.publishers.reduce(
      (accumulator: string, publisherId: V4UUID) => {
        const topicInfo = getTopicDependency(this.workspaceId, publisherId);
        if (!topicInfo) return accumulator;
        const msgInfo = getMsgDependency(
          this.workspaceId,
          topicInfo.topic.msgType
        );
        if (!msgInfo) return accumulator;
        return (
          accumulator +
          `    ${toSnakeCase(
            topicInfo.topic.name
          )}_pub = this->create_publisher<${msgInfo.packageName}::msg::${
            msgInfo.msg.name
          }>("${topicInfo.topic.getNameNoFirstSlash()}", 10);\n`
        );
      },
      ''
    );
  }

  getServerInitialisations(): string {
    return getNodeServiceDependencies(
      this.workspaceId,
      this.node.id
    ).servers.reduce((accumulator: string, srvInfo: ServiceDependencyInfo) => {
      return (
        accumulator +
        `    ${toSnakeCase(
          srvInfo.srv.name
        )}_server = this->create_service<${toSnakeCase(
          srvInfo.packageName
        )}::srv::${toCamelCase(srvInfo.srv.name)}>(
        "${toSnakeCase(srvInfo.srv.name)}", 
        std::bind(&${toCamelCase(this.node.name)}::${toSnakeCase(
          srvInfo.srv.name
        )}_srv_callback, this, std::placeholders::_1, std::placeholders::_2)
    );`
      );
    }, '');
  }

  getClientInitialisations(): string {
    return getNodeServiceDependencies(
      this.workspaceId,
      this.node.id
    ).clients.reduce((accumulator: string, srvInfo: ServiceDependencyInfo) => {
      return (
        accumulator +
        `    ${toSnakeCase(
          srvInfo.srv.name
        )}_client = this->create_client<${toSnakeCase(
          srvInfo.packageName
        )}::srv::${toCamelCase(srvInfo.srv.name)}>("${toSnakeCase(
          srvInfo.srv.name
        )}");`
      );
    }, '');
  }

  getCallbackImplementations(): string {
    return this.node.subscribers.reduce(
      (accumulator: string, subscriberId: V4UUID) => {
        const topicInfo = getTopicDependency(this.workspaceId, subscriberId);
        if (!topicInfo) return accumulator;
        const msgInfo = getMsgDependency(
          this.workspaceId,
          topicInfo.topic.msgType
        );
        if (!msgInfo) return accumulator;
        const callbackInfo = this.node.customText.callbacks.find(
          (callbackInfo) =>
            callbackInfo.name ===
            `${toSnakeCase(topicInfo.topic.name)}_callback`
        );
        const callbackText = (callbackInfo && callbackInfo.text) || '';
        return (
          accumulator +
          `void ${toCamelCase(this.node.name)}::${toSnakeCase(
            topicInfo.topic.name
          )}_callback(const std::shared_ptr<${msgInfo.packageName}::msg::${
            msgInfo.msg.name
          }> msg) {
    // customize ${toSnakeCase(topicInfo.topic.name)}_callback below
${callbackText}
    // end ${toSnakeCase(topicInfo.topic.name)}_callback customization\n}\n`
        );
      },
      ''
    );
  }

  getSrvCallbackImplementations(): string {
    return getNodeServiceDependencies(
      this.workspaceId,
      this.node.id
    ).servers.reduce((accumulator: string, srvInfo: ServiceDependencyInfo) => {
      let callbackInfo = this.node.customText.callbacks.find(
        (customCallbackInfo) =>
          customCallbackInfo.name ===
          `${toSnakeCase(srvInfo.srv.name)}_srv_callback`
      );
      let customCallbackText = (callbackInfo && callbackInfo.text) || '';
      return (
        accumulator +
        `void ${toCamelCase(this.node.name)}::${toSnakeCase(
          srvInfo.srv.name
        )}_srv_callback(
    const std::shared_ptr<${toSnakeCase(
      srvInfo.packageName
    )}::srv::${toCamelCase(srvInfo.srv.name)}::Request> request,
    std::shared_ptr<${toSnakeCase(srvInfo.packageName)}::srv::${toCamelCase(
          srvInfo.srv.name
        )}::Response> response
) {
    // customize ${toSnakeCase(srvInfo.srv.name)}_srv_callback below
${customCallbackText}
    // end ${toSnakeCase(srvInfo.srv.name)}_srv_callback customization\n}\n`
      );
    }, '');
  }
}
